// # services
import { auth, clipboard, notifications } from '@/services';

angular.module('app')
    .controller('manageEventController', manageEventController);

manageEventController.$inject = [
    '$scope',
    '$rootScope',
    '$uibModalInstance',
    'eventId',
    'EventsApi',
    'EventAttendeesApi',
    'locations',
    'ISO3166',
    'handleError',
    'UploadsApi',
    'stripeKey',
    'confirmDialog',
    'Helpers',
    'Subevents',
    'thisSiteSettings',
    'CategoryTypesApi',
    'CategoriesApi',
    'rolesApi',
    'plansApi',
    'GroupsApi',
    'PollsApi',
];

function manageEventController(
    $scope,
    $rootScope,
    $uibModalInstance,
    eventId,
    EventsApi,
    EventAttendeesApi,
    locations,
    countries,
    handleError,
    UploadsApi,
    stripeKey,
    confirmDialog,
    Helpers,
    Subevents,
    thisSiteSettings,
    CategoryTypesApi,
    CategoriesApi,
    rolesApi,
    plansApi,
    GroupsApi,
    PollsApi,
) {
    const vm = this;

    // properties
    vm.permissionChecks = {
        canManageEvents: auth.hasPermission('events.manage'),
    };
    vm.isLoading = false;
    vm.isLoadingMessage = 'Loading...';
    vm.eventId = eventId;
    vm.createNewEvent = !eventId;
    vm.thisUser = $rootScope.thisUser;
    vm.activeTab = 'about';
    vm.formErrors = {};
    vm.stripeKey = stripeKey;
    vm.categoryTypes = []; // each one with "categories" and "selectedCategoryId" array

    /**
     * The current state of the banner image.
     *
     * @var {{ data: null | string, uploaded: boolean }}
     */
    vm.image = {
        data: null,
        uploaded: false,
    };
    vm.event = {
        status: 'pending',
        price: 0,
        address: {},
        contactables: {},
        description: '<p><br></p>',
        payment_required: false,
        private: false,
        startsAt: new Date().setMinutes(0),
        settings: {
            hide_attendees: false,
        },
        categories: [],
        custom_attrs: [],
        group_id: null,
        dates: [
            {
                repeat: 'never',
                repeat_times: 0,
            },
        ],
    };
    vm.categoryTypes = []; // each one with "categories" and "selectedCategoryId" array

    vm.statusOptions = [
        { label: 'Pending', value: 'pending' },
        { label: 'Approved', value: 'approved' },
        { label: 'Rejected', value: 'rejected' },
    ];
    vm.endDateOptions = [
        { label: 'No', value: false },
        { label: 'Yes', value: true },
    ];
    vm.countries = countries.countryToCode;
    vm.imageUploadOptions = {
        resize: true,
        resizeShape: 'rectangle',
        resizeSize: {
            w: 1250,
            h: 640,
        },
        resizeAspectRatio: 0,
    };
    vm.addAttendeeData = {
        create_event_order: false,
        email: null,
        full_name: null,
        ticket_id: null,
    };
    vm.subevents = [];
    vm.formfields = [];
    vm.categoryTypes = []; // each one with "categories" and "selectedCategoryId" array

    vm.event.tickets = [
        {
            'label': '',
            'price': 0,
            'total_available_tickets': 0,
            'role_id': '',
            'available_until': '',
        },
    ];

    vm.availableTickets = [];
    vm.groups = [];

    vm.events = [];

    // methods
    vm.close = $uibModalInstance.close;
    vm.copyPublicURLToClipboard = copyPublicURLToClipboard;
    vm.handleDescriptionChange = handleDescriptionChange;
    vm.handleImage = handleImage;
    vm.handleImageError = handleImageError;
    vm.searchLocations = searchLocations;
    vm.selectLocation = selectLocation;
    vm.handleSubmit = handleSubmit;
    vm.deleteEvent = deleteEvent;
    vm.addAttendee = addAttendee;
    vm.removeAttendee = removeAttendee;
    vm.addSubevent = addSubevent;
    vm.editSubevent = editSubevent;
    vm.addFF = addFF;
    vm.removeFF = removeFF;
    vm.getSubevents = getSubevents;
    vm.getAttendees = getAttendees;
    vm.currencySymbol = thisSiteSettings.currency_symbol;

    vm.eventAttrs = thisSiteSettings.custom_event_attrs;

    vm.getCategoryTypes = getCategoryTypes;
    vm.getCategories = getCategories;
    vm.setCategories = setCategories;
    vm.addTicket = addTicket;
    vm.deleteTicket = deleteTicket;
    vm.getRoles = getRoles;

    vm.ff_edited = false;

    /**
     * Initialises the controller.
     *
     * @returns {Promise<void>}
     */
    async function bootstrap(): Promise<void>  {
        try {
            vm.isLoading = true;
            await getCategoryTypes();

            if (!vm.createNewEvent) {
                await getEvent();
                await getSubevents();
                await getPolls();
            } else {
                await getRoles();
                await getPlans();
                await getGroups();
            }


            $scope.$watch('activeTab', activeTab => {
                if (activeTab === 'location') {
                    // Update the map
                    locations.makeMap(vm.event.latlng, '#create-location-map');
                }
            });

            // Catch the modal being closed for any reason
            $scope.$on('modal.closing', (event, reason, closed) => {
                if (!closed && !vm.forceClose) {
                    return event.preventDefault();
                }

                if (vm.forceClose) {
                    return true;
                }
            });

            vm.isLoading = false;
            $scope.$apply();
        } catch (error) {
            vm.isLoading = false;
            throw error;
        }
    }

    /**
     * Copies the event's "link" property to the users' clipboard.
     *
     * @returns {void}
     */
    function copyPublicURLToClipboard(): void {
        if (!vm.event.link) {
            return;
        }

        try {
            clipboard.insert(vm.event.link);
            notifications.display({
                message: vm.event.link,
                title: 'Event link was copied to your clipboard',
                theme: 'success',
            });
        } catch (error) {
            notifications.display({
                message: 'Error',
                title: 'The link could not be copied to your clipboard',
                theme: 'error',
            });
        }
    }

    /**
     * Loads an event to be edited
     *
     * @return {Promise<void>}
     */
    async function getEvent(): Promise<void> {
        return new Promise((resolve, reject) => {
            vm.isLoadingMessage = 'Loading Event...';
            vm.isLoading = true;

            EventsApi.get({
                id: vm.eventId,
                with: '[attendees, tickets, children]',
            }, response => {
                vm.event = transformEvent(response.data);

                formatTickets(vm.event);
                getRoles();
                getPlans();
                getGroups();
                getAvailableTickets();
                vm.isLoading = false;

                vm.formfields = JSON.parse(vm.event.custom_attrs);

                setCategories(vm.event.categories.map(function (d) { return d.id; }));
                resolve();
            }, err => {
                vm.isLoading = false;
                handleError(err);
            });
        });
    }

    /**
     * Updates the event description
     *
     * @param {string} description html
     * @returns {void}
     */
    function handleDescriptionChange(description) {
        vm.event.description = description;
    }

    /**
     * Handles the image chosen by the user
     *
     * @param  {string} file - base64 image
     * @return {void}
     */
    function handleImage(file) {
        vm.image.data = file;
        vm.image.uploaded = false;
    }

    /**
     * Shows file errors
     * @param  {object} imageError - provided as  {image: '[error msg]'}
     * @return {void}
     */
    function handleImageError(imageError) {
        vm.formErrors = angular.merge(vm.formErrors, imageError);
    }

    /**
     * Gets google place suggestions and makes them available to the scope
     * @param  {string} searchString - Location search query
     * @param  {bool}   updateMapWithFirstResult - whether or not to update the map using the first returned result
     * @return {void}
     */
    function searchLocations(searchString, updateMapWithFirstResult) {
        locations.find(searchString)
            .then(locationResults => {
                vm.locationSuggestions = locationResults;

                // Update the map?
                if (updateMapWithFirstResult && locationResults[0]) {
                    locations.getDetails(locationResults[0].placeId).then(locationDetails => {
                        // Update location coordinates
                        vm.event.latlng = locationDetails.coords;

                        // Update map
                        locations.makeMap(locationDetails.coords, '#create-location-map');
                    });
                }
            }, err => {
                handleError(err);
            });
    }

    /**
     * Gets details of the selected location and address to the event
     * @param  {object} location - Location object as provided by locations.find
     * @return {void}
     */
    function selectLocation(location) {
        locations.getDetails(location.placeId)
            .then(locationDetails => {
                // Add location address to scope
                vm.event.address = locationDetails.address;
                vm.event.latlng = locationDetails.coords;

                // Add map to page
                locations.makeMap(locationDetails.coords, '#create-location-map');
            }, err => {
                handleError(err);
            });
    }

    /**
     * Saves the event to the API.
     *
     * @returns {Promise<record<string, any>>} The saved event
     */
    async function saveEvent(): Promise<Record<string, any>> {
        return new Promise((resolve, reject) => {
            // Don't edit the original event
            const eventCopy = { ...vm.event };

            // Which api method to use.
            const method: 'save' | 'update' = vm.event.id ? 'update' : 'save';

            // URL params to send with the request.
            const params: Record<string, any> = method === 'save' ? { id: vm.event.id } : {};


            // Set relevant loading message
            vm.isLoadingMessage = (method === 'save' ? 'Creating new event...' : 'Saving event...');

            // Convert dates to utc and format
            eventCopy.starts_at = window.moment.tz(eventCopy.startsAt, 'Europe/London').tz('UTC').format(Helpers.isoFormat);
            // Old events might have no end date
            eventCopy.ends_at = (eventCopy.endsAt ? window.moment.tz(eventCopy.endsAt, 'Europe/London').tz('UTC').format(Helpers.isoFormat) : null);

            // (bool) & (int) the payment_required field
            eventCopy.payment_required = +eventCopy.payment_required;

            // Make API request
            EventsApi[method](params, eventCopy, response => {
                resolve(response);
            }, reject).suppressValidationAlerts();
        });
    }

    /**
     * Updates everything when the form is submitted.
     *
     * @return {Promise<void>}
     */
    async function handleSubmit(): Promise<void> {
        let firstevent;
        try {
            vm.isLoading = true;

            // save event and update local copy
            //change event to events
            const savedEvents = await saveEvent();

            vm.events = savedEvents.data;
            for (const event of vm.events) {
                //loop over each event in the events
                vm.event = transformEvent(event);

                // upload the image if required
                await saveImage();

                // reset saving state
                vm.isLoading = false;
                vm.formErrors = {};
                vm.createNewEvent = false;

                getAttendees();
                $scope.$digest(); // tell angular that something changed 🙄
            }
            notifications.display({
                theme: 'success',
                title: 'Saved',
                message: 'The event has been saved.',
            });

            if (savedEvents.event != undefined) {
              vm.event = transformEvent(savedEvents.event);
            }

        } catch (err) {
            vm.isLoading = false;

            let errorMessage = '';

            if (err.data) {
                vm.formErrors = err.data;
                errorMessage = err.data.message;
            }

            notifications.display({
                theme: 'error',
                title: 'Error saving event',
                message: errorMessage,
            });

            getAttendees();
            $scope.$digest();
        }
    }

    /**
     * Uploads the banner image if it has been changed.
     *
     * @returns {Promise<Record<string, any>>} The updated event.
     */
    async function saveImage(): Promise<Record<string, any> | void> {
        const promise = new Promise<Record<string, any> | void>(resolve => {
            if (!vm.image.data || vm.image.uploaded) {
                // No image set, or already uploaded. No need to do anything.
                return resolve();
            }

            async function handleUploadSuccess(result: { path: string, src: string }): Promise<void> {
                if (result && result.path) {
                    // update event and save
                    vm.event.img = result.path;
                    resolve(await saveEvent());
                } else {
                    throw new Error('Invalid response from image upload endpoint.');
                }
            }

            const handleUploadError = (err: any): void => {
                throw (err);
            };

            const handleUploadProgress = (progress: number): void => {
                vm.isLoadingProgress = progress;

                if (progress === 100) {
                    vm.isLoadingProgress = 0;
                    vm.isLoadingMessage = 'Saving image...';
                }
            };

            vm.isLoadingMessage = 'Uploading image...';
            UploadsApi
                .uploadImage(vm.image.data, 'site')
                .then(handleUploadSuccess, handleUploadError, handleUploadProgress);
        });

        return promise;
    }

    /**
     * Deletes the event and closes the modal
     * @return {void}
     */
    function deleteEvent() {
        confirmDialog('Are you sure you want to delete this event?')
            .result.then(confirm => {
                if (confirm) {
                    vm.isLoadingMessage = 'Deleting...';
                    vm.isLoading = true;

                    EventsApi.delete({
                        id: vm.event.id,
                    }, function handleDeleteEventResponse() {
                        vm.forceClose = true;
                        $uibModalInstance.dismiss('deleted');
                    }, function handleEventError() {
                        vm.isLoading = false;
                    });
                }
            });
    }

    function addAttendee() {
        const data = {
            ...vm.addAttendeeData,
            event_id: vm.event.id,
        };

        EventAttendeesApi.post(
            data,
            response => {
                vm.event.attendees.push(response.data);
            },
        );
    }

    function removeAttendee(id, index) {
        confirmDialog('Are you sure you want to remove this attendee?')
            .result.then(confirm => {
                if (confirm) {
                    EventAttendeesApi.delete({ id, index }, () => {
                        // success
                        vm.forceClose = true;
                        vm.event.attendees.splice(index, 1);
                    }, () => {
                        // error
                        vm.isLoading = false;
                    });
                }
            });
    }

    /**
     * Save the event as a draft and then once it has been saved as a draft,
     * bring up a modal with the event that has just been saved as a draft
     * inserted as the parent_id of the now child event.
     *
     * @return {void}
     */
    function addSubevent() {
        if (!vm.eventId) {
            const orig = (vm.event.status === 'draft') ? 'pending' : vm.event.status;

            // Convert dates to utc and format
            vm.event.starts_at = window.moment.tz(vm.event.startsAt, 'Europe/London').tz('UTC').format(Helpers.isoFormat);
            vm.event.ends_at = (vm.event.endsAt ? window.moment.tz(vm.event.endsAt, 'Europe/London').tz('UTC').format(Helpers.isoFormat) : undefined); // old events might have no end date

            vm.event.status = 'draft';

            return EventsApi.save({}, vm.event, response => {
                vm.eventId = response.data.id;
                vm.event = response.data;

                // This is reset so that on the full save it can become pending/approved/whatever.
                vm.event.status = orig;
                vm.createNewEvent = false;

                Subevents.create(vm.eventId).result.then(() => {
                    vm.getSubevents();
                });
            }, () => {
                vm.event.status = orig;
            });
        }

        return Subevents.create(vm.eventId).result.then(() => {
            vm.getSubevents();
        });
    }

    /**
     * Bring up a modal which will be responsible for editing the event.
     *
     * @param  {Integer} subeventId
     * @return {void}
     */
    function editSubevent(subeventId) {
        return Subevents.edit(vm.eventId, subeventId).result.then(() => {
            vm.getSubevents();
        });
    }

    function addFF(textdata) {

        if (textdata == null || textdata == undefined || textdata == "") {
          return;
        }

        const searchIndex = vm.eventAttrs.findIndex((attrs) => attrs.key == textdata);

        const data = vm.eventAttrs[searchIndex];

        const original_attrs = [];

        //let data = $scope.event.fieldselect;
        let x;
        try {
            x = JSON.parse(vm.event.custom_attrs);
        } catch (e) {
            x = vm.event.custom_attrs;
        }

        if (vm.ff_edited) {
            x = vm.event.custom_attrs;
        }

        if (Array.isArray(x) && x.length > 0) {
            let startLength = 0;
            x.forEach((element) => {
                if (element != undefined) {
                    //check if this already exists in the array
                    original_attrs.push(element);
                    startLength++;
                }
            });
        }

        const index = original_attrs.findIndex(object => object.key === data.key);

        if (index === -1) {
            original_attrs.push(data);
        }

        vm.event.custom_attrs = original_attrs;
        vm.formfields = original_attrs;
    }

    function removeFF(data) {
        const index = vm.formfields.indexOf(data);
        const index2 = vm.event.custom_attrs.indexOf(data);
        vm.ff_edited = true;
        if (index > -1) { // only splice array when item is found
            vm.formfields.splice(index, 1); // 2nd parameter means remove one item only
        }
        vm.event.custom_attrs = vm.formfields;
    }

    /**
     * Get a list of subevents which are members of the current event which
     * is selected.
     *
     * @return {[type]}
     */
    function getSubevents() {
        vm.isLoading = true;
        EventsApi.get({ parent_id: vm.eventId }, response => {
            vm.subevents = response.data;
            vm.subevents.map(subevent => {
                const startsAt = window.moment(subevent.starts_at);
                const endsAt = window.moment(subevent.ends_at);

                if (endsAt.isValid()) {
                    subevent.information = [
                        startsAt.format('ddd Do MMM YYYY hh:mma'),
                        startsAt.isSame(endsAt, 'day') ? endsAt.format('hh:mma') : endsAt.format('ddd Do MMM YYYY hh:mma'),
                    ].join(' - ');
                } else {
                    subevent.information = startsAt.format('ddd Do MMM YYYY hh:mma');
                }
            });
            vm.isLoading = false;
        }, error => {
            vm.isLoading = false;
            handleError(error);
        });
    }

    /**
     * Modifies an event model from the server with any changes we need in our controller.
     *
     *
     * @param {Record<string, any>} event
     * @returns {Record<string, any>} transformed event
     */
    function transformEvent(event: Record<string, any>): Record<string, any> {
        return {
            ...event,
            private: +event.private,
            dates: [
                {
                    starts_at: window.moment.tz(event.starts_at, 'UTC').tz('Europe/London').toDate(),
                    ends_at: window.moment.tz(event.ends_at, 'UTC').tz('Europe/London').toDate(),
                },
            ],
        };
    }

    /**
     * Modifies an events tickets
     *
     *
     * @param {Record<string, any>} event
     * @returns {Void}
     */
    function formatTickets(event: Record<string, any>) {
        event.tickets.forEach(ticket => {
            ticket.available_until = ticket.available_until ? new Date(ticket.available_until) : null;
            ticket.role_id = ticket.role_id ? ticket.role_id.toString() : null;
            ticket.plan_id = ticket.plan_id ? ticket.plan_id.toString() : null;
        });
    }

    /**
    * Retrieves category types from the API and caches to scope
    *
    * @returns {Void}
    */
    function getAttendees() {
        if (vm.eventId) {
            vm.isLoading = true;

            EventAttendeesApi.get(
                {
                    event_id : vm.eventId,
                },
                function handleGetAttendeesSuccess(response) {
                    vm.event.attendees = response.data;
                    vm.isLoading = false;
                },
                function handleGetAttendeesError() {vm.isLoading = false;},
            );
        }
    }


    /**
     * Retrieves category types from the API
     *
     * @returns {Promise<void>}
     */
    function getCategoryTypes(): Promise<void> {
        return new Promise((resolve, reject) => {
            CategoryTypesApi.query(
                { 'categorizables[]': ['events'] },
                async (response: any) => {
                    vm.categoryTypes = response;
                    await getCategories();
                    resolve();
                },
                reject,
            );
        });
    }

    /**
     * Retrieves categories from the API
     *
     * @returns {Promise<void>}
     */
    function getCategories(): Promise<void> {
        return new Promise((resolve, reject) => {
            CategoriesApi.query(response => {
                vm.categories = response;
                angular.forEach(vm.categoryTypes, categoryType => {
                    const cats = vm.categories.filter(cat => {
                        return cat.type_id === categoryType.id;
                    });

                    categoryType.categories = cats;
                });
                resolve();
            }, reject);
        });
    }

    /**
     * Replaces selected categories on the news object.
     *
     * @param {number[]} categoryIds
     * @returns {void}
     */
    function setCategories(categoryIds: number[]): void {
        vm.event.categories = categoryIds;

        $scope.$apply();
    }


    /**
    * Adds another ticket input
    * @returns {Void}
    */
    function addTicket(e) {
        e.preventDefault();

        vm.event.tickets.push({
            'label': '',
            'price': 0,
            'total_available_tickets': 0,
            'role_id': '',
            'available_until': '',
        });
    }

    /**
     * Deletes ticket
     * @returns {Void}
     */
    function deleteTicket(e, ticket) {
        e.preventDefault();

        //find that hash key in the events tickets
        const index = vm.event.tickets.findIndex(obj => obj.$$hashKey === ticket.$$hashKey);

        //remove that object from the events tickets array
        if (index !== -1) {
            vm.event.tickets.splice(index, 1);
        }
        //This hides the ticket in dispaly without reloading the page
        ticket.delete = true;
    }

    /**
     * Getx availale tickets
     * @returns {Void}
     */
    function getAvailableTickets() {
        vm.event.tickets.forEach(ticket => {
            const ticketsLeft = ticket.total_available_tickets - ticket.total_redeemed_tickets;

            if (ticketsLeft > 0 && (ticket.available_until ? new Date() < new Date(ticket.available_until) : true)) {
                vm.availableTickets.push(ticket);
            }
        });
    }

    /**
     * Get the sites roles for ticket dropdown
     * @returns {Void}
    */
    function getRoles() {
        rolesApi.get().$promise
            .then(response => {
                vm.roles = response.data;
            });
    }

    /**
     * Get the sites plans for ticket dropdown
     * @returns {Void}
    */
    function getPlans() {
        plansApi.get().$promise
            .then(response => {
                vm.plans = response.data;
            });
    }

    /**
     * Get the sites roles for ticket dropdown
     * @returns {Void}
     */
    function getGroups() {
        GroupsApi.get().$promise
            .then(response => {
                vm.groups = response.data;
            });
    }


    /**
     * Retrieves polls
     *
     * @return {void}
    */
         function getPolls() {
            PollsApi.get({
                status: 'approved',
            }, response => {
                vm.polls = response.data;
            });
        }

    bootstrap();

    (function () {
        getAttendees();
    })();
}
